import { useState, useEffect } from 'react';
import { apiClient } from '../../../services/apiClient';
import PortalModal from '../../common/PortalModal/PortalModal';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import ToggleSwitch from '../../common/ToggleSwitch/ToggleSwitch';
import '../../Organization/InformationSources.css';

const DEFAULT_SLACK_CONFIG = {
  channels_to_index: [],
  allowed_write_channels: [],
  message_lookback_hours: 24,
  workspace_name: ''
};

function SlackModal({ isOpen, masterEnabled, onClose, onSave }) {
  const [handlerRunning, setHandlerRunning] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [tokens, setTokens] = useState({ bot_token: '', app_token: '' });
  const [slackConfig, setSlackConfig] = useState(DEFAULT_SLACK_CONFIG);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);

  // Slack account linking
  const [linkedSlackId, setLinkedSlackId] = useState(null);
  const [slackUserIdInput, setSlackUserIdInput] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadStatus();
      loadConfig();
      loadChannels();
      loadLink();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const status = await apiClient.getSlackStatus();
      setHandlerRunning(status.handler_running ?? false);
    } catch (err) {
      console.error('Failed to load Slack status:', err);
    } finally {
      setLoading(false);
    }
  };

  const waitForSlackStopped = async (attempts = 10, delayMs = 300) => {
    let latest = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      latest = await apiClient.getSlackStatus();
      setHandlerRunning(latest.handler_running ?? false);

      if (!latest.enabled && !latest.handler_running) {
        return latest;
      }

      if (attempt < attempts - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }
    }

    return latest;
  };

  const loadLink = async () => {
    try {
      const res = await apiClient.getSlackLink();
      setLinkedSlackId(res.linked ? res.slack_user_id : null);
    } catch (err) {
      console.error('Failed to load Slack link:', err);
    }
  };

  const loadConfig = async () => {
    try {
      const config = await apiClient.getSlackConfig();
      setSlackConfig({
        channels_to_index: config.channels_to_index || [],
        allowed_write_channels: config.allowed_write_channels || [],
        message_lookback_hours: config.message_lookback_hours || 24,
        workspace_name: config.workspace_name || ''
      });
    } catch (err) {
      console.error('Failed to load Slack config:', err);
    }
  };

  const loadChannels = async () => {
    try {
      setChannelsLoading(true);
      const response = await apiClient.getSlackChannels();
      setAvailableChannels(response.channels || []);
    } catch (err) {
      console.error('Failed to load Slack channels:', err);
      setAvailableChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  };

  const toggleChannelSelection = (field, channelId) => {
    setSlackConfig((prev) => {
      const currentValues = prev[field] || [];
      const nextValues = currentValues.includes(channelId)
        ? currentValues.filter((id) => id !== channelId)
        : [...currentValues, channelId];

      return {
        ...prev,
        [field]: nextValues
      };
    });
  };

  const handleToggle = async (val) => {
    if (!masterEnabled) return;
    const prev = handlerRunning;
    setToggling(true);
    try {
      const response = val
        ? await apiClient.startSlackHandler()
        : await apiClient.stopSlackHandler();

      setHandlerRunning(response.running ?? val);
      if (val) {
        const status = await apiClient.getSlackStatus();
        setHandlerRunning(status.handler_running ?? val);
      } else {
        await waitForSlackStopped();
      }
      if (onSave) onSave();
    } catch (err) {
      console.error('Failed to toggle Slack handler:', err);
      setHandlerRunning(prev);
    } finally {
      setToggling(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!slackUserIdInput.trim()) return;
    setLinking(true);
    try {
      await apiClient.linkSlackUser(slackUserIdInput.trim());
      setLinkedSlackId(slackUserIdInput.trim());
      setSlackUserIdInput('');
    } catch (err) {
      console.error('Failed to link Slack account:', err);
      alert('Failed to link account: ' + err.message);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async () => {
    setLinking(true);
    try {
      await apiClient.unlinkSlackUser();
      setLinkedSlackId(null);
    } catch (err) {
      console.error('Failed to unlink:', err);
    } finally {
      setLinking(false);
    }
  };

  const handleSaveConfig = async () => {
    const updates = {
      channels_to_index: slackConfig.channels_to_index,
      allowed_write_channels: slackConfig.allowed_write_channels,
      message_lookback_hours: Number(slackConfig.message_lookback_hours) || 24,
      workspace_name: slackConfig.workspace_name.trim()
    };
    if (tokens.bot_token) updates.bot_token = tokens.bot_token;
    if (tokens.app_token) updates.app_token = tokens.app_token;
    try {
      setSaving(true);
      await apiClient.updateSlackConfig(updates);
      setTokens({ bot_token: '', app_token: '' });
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save Slack tokens:', err);
      alert('Failed to save tokens: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const footerContent = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSaveConfig} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  return (
    <PortalModal
      isOpen={isOpen}
      onClose={onClose}
      title="Slack Bot"
      footer={footerContent}
      size="medium"
    >
      <div className="form-grid">
        {/* Kill switch */}
        <div
          className="ui-form-group"
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-surface)',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid var(--border-subtle)'
          }}
        >
          <div>
            <label className="ui-label" style={{ marginBottom: 0, color: 'var(--text-primary)' }}>
              Slack Bot Active
            </label>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {masterEnabled
                ? 'Start or stop the Socket Mode bot at runtime'
                : 'Turn on Agentic Mode to control the Slack bot'}
            </p>
          </div>
          <ToggleSwitch
            checked={handlerRunning}
            onChange={handleToggle}
            disabled={!masterEnabled || toggling || loading}
          />
        </div>

        {/* Status indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: handlerRunning ? 'var(--success, #10b981)' : 'var(--text-muted, #6b7280)',
              display: 'inline-block',
              flexShrink: 0
            }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>
            {loading ? 'Checking...' : handlerRunning ? 'Bot is running' : 'Bot is stopped'}
          </span>
        </div>

        {/* Link Slack account */}
        <div
          className="ui-form-group"
          style={{
            background: 'var(--bg-surface)',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid var(--border-subtle)'
          }}
        >
          <label className="ui-label" style={{ color: 'var(--text-primary)' }}>
            Link Your Slack Account
          </label>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Links your OmniContext identity to your Slack profile so the bot can access your personal context.
            For now, the Slack account email must match your OmniContext login email.
          </p>

          {linkedSlackId ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ color: 'var(--success, #10b981)' }}>
                  <i className="fas fa-check-circle" />
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  Linked as <code style={{ fontSize: '0.8rem' }}>{linkedSlackId}</code>
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleUnlink} disabled={linking}>
                Unlink
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="U0XXXXXXXXX"
                value={slackUserIdInput}
                onChange={(e) => setSlackUserIdInput(e.target.value)}
                disabled={linking}
                style={{
                  flex: 1,
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.85rem',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleLinkAccount}
                disabled={linking || !slackUserIdInput.trim()}
              >
                {linking ? 'Linking...' : 'Link'}
              </Button>
            </div>
          )}
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Find your Slack User ID: click your name in Slack → View profile → More → Copy member ID
          </p>
        </div>

        {/* Token fields */}
        <Input
          label="Bot Token (xoxb-...)"
          type="password"
          value={tokens.bot_token}
          onChange={(e) => setTokens(prev => ({ ...prev, bot_token: e.target.value }))}
          placeholder="Leave blank to keep existing token"
          disabled={saving}
          helperText="Bot User OAuth Token from Slack app settings"
        />

        <Input
          label="App Token (xapp-...)"
          type="password"
          value={tokens.app_token}
          onChange={(e) => setTokens(prev => ({ ...prev, app_token: e.target.value }))}
          placeholder="Leave blank to keep existing token"
          disabled={saving}
          helperText="App-Level Token for Socket Mode — requires connections:write scope"
        />

        <Input
          label="Workspace Name"
          type="text"
          value={slackConfig.workspace_name}
          onChange={(e) => setSlackConfig((prev) => ({ ...prev, workspace_name: e.target.value }))}
          placeholder="Optional label for this Slack workspace"
          disabled={saving}
          helperText="Used as a friendly label in configuration responses."
        />

        <div
          className="ui-form-group"
          style={{
            background: 'var(--bg-surface)',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid var(--border-subtle)'
          }}
        >
          <label className="ui-label" style={{ color: 'var(--text-primary)' }}>
            Slack Channel Access
          </label>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Pick channels the bot can index for summaries/search and channels it is allowed to post into.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', gap: '0.75rem', marginBottom: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <strong style={{ color: 'var(--text-secondary)' }}>Channel</strong>
            <strong style={{ color: 'var(--text-secondary)' }}>Index</strong>
            <strong style={{ color: 'var(--text-secondary)' }}>Post</strong>
          </div>

          {channelsLoading ? (
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading available Slack channels...</p>
          ) : availableChannels.length > 0 ? (
            <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              {availableChannels.map((channel) => (
                <div
                  key={channel.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                    gap: '0.75rem',
                    alignItems: 'center',
                    padding: '0.6rem 0.75rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-subtle)',
                    background: 'rgba(255,255,255,0.03)'
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '0.85rem', fontWeight: 600 }}>
                      #{channel.name}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                      {channel.id}{channel.is_private ? ' • Private' : ''}
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={slackConfig.channels_to_index.includes(channel.id)}
                    onChange={() => toggleChannelSelection('channels_to_index', channel.id)}
                    disabled={saving}
                  />

                  <input
                    type="checkbox"
                    checked={slackConfig.allowed_write_channels.includes(channel.id)}
                    onChange={() => toggleChannelSelection('allowed_write_channels', channel.id)}
                    disabled={saving}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              No joined Slack channels were returned. Invite the bot to a channel in Slack to manage it here.
            </p>
          )}

          <div className="ui-form-group" style={{ marginBottom: 0 }}>
            <label className="ui-label" style={{ color: 'var(--text-primary)' }}>
              Message Lookback Default (Hours)
            </label>
            <input
              type="number"
              min="1"
              max="168"
              value={slackConfig.message_lookback_hours}
              onChange={(e) => setSlackConfig((prev) => ({ ...prev, message_lookback_hours: e.target.value }))}
              disabled={saving}
              style={{
                width: '100%',
                padding: '0.65rem 0.75rem',
                fontSize: '0.9rem',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '8px',
                color: 'var(--text-primary)',
                outline: 'none'
              }}
            />
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Controls how far back Slack indexing and summary fetches will search by default.
            </p>
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

export default SlackModal;
